import { useState, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FUNCTIONAL_SAFETY_CATEGORY, FUNCTIONAL_SAFETY_CHANNEL_TYPES, getMTTFCategory, calculateMultiChannelMTTFd } from '../utils/constants';

const CHANNEL_TYPES = FUNCTIONAL_SAFETY_CHANNEL_TYPES;

// Categories that should show TE and OTE channels
const CATEGORIES_WITH_TE_OTE = [FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_2M];

// Categories that should show Channel 2 (INPUT2, LOGIC2, OUTPUT2)
const CATEGORIES_WITH_CHANNEL_2 = [
  FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_3,
  FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_4,
];

/**
 * Custom hook for MTTF component logic
 * Handles state management, calculations, and data organization
 */
const useMTTF = ({ assessment, groupedMTTFData, createMTTF, updateMTTF, deleteMTTF, isCreating, isUpdating, isDeleting }) => {
  const [newRows, setNewRows] = useState({
    input: [],
    logic: [],
    output: [],
    te: [],
    ote: [],
    input2: [],
    logic2: [],
    output2: [],
  });

  const [editingRow, setEditingRow] = useState(null);
  const [editedData, setEditedData] = useState({});

  // Check if TE and OTE should be shown based on category
  const shouldShowTeOte = useMemo(() => {
    return assessment?.category && CATEGORIES_WITH_TE_OTE.includes(assessment.category);
  }, [assessment?.category]);

  // Check if Channel 2 should be shown based on category
  const shouldShowChannel2 = useMemo(() => {
    return assessment?.category && CATEGORIES_WITH_CHANNEL_2.includes(assessment.category);
  }, [assessment?.category]);

  // Calculate MTTFd for a specific component type using MTTFd,i
  // Formula: MTTFd = 1/∑(1/MTTFd,i * quantity)
  const mttfdSum = useCallback((type) => {
    const savedRows = groupedMTTFData?.[type] || [];
    const unsavedRows = newRows[type] || [];
    const allRows = [...savedRows, ...unsavedRows];

    const inverseSum = allRows.reduce((acc, curr) => {
      const quantity = Number(curr['quantity']) || 0;
      const mttfdij = Number(curr['mttfdij']) || 0; // Use MTTFd,i, not MTTFi

      if (mttfdij === 0) return acc;
      return acc + (1 / mttfdij) * quantity;
    }, 0);

    return inverseSum > 0 ? 1 / inverseSum : 0;
  }, [groupedMTTFData, newRows]);

  // Calculate MTTFd for a channel by combining multiple component types
  // Formula: MTTFd = 1/∑(1/MTTFd,i * quantity) for all components in the channel
  const calculateChannelMTTFd = useCallback((types) => {
    const allComponents = types.flatMap(type => {
      const savedRows = groupedMTTFData?.[type] || [];
      const unsavedRows = newRows[type] || [];
      return [...savedRows, ...unsavedRows];
    });

    const inverseSum = allComponents.reduce((acc, curr) => {
      const quantity = Number(curr['quantity']) || 0;
      const mttfdij = Number(curr['mttfdij']) || 0;

      if (mttfdij === 0) return acc;
      return acc + (1 / mttfdij) * quantity;
    }, 0);

    return inverseSum > 0 ? 1 / inverseSum : 0;
  }, [groupedMTTFData, newRows]);

  // Channel 1 MTTF calculation (Input, Logic, Output combined)
  // According to document: MTTFd = 1/∑(1/MTTFd,i) for all components in the channel
  const mttfdChannel1 = useMemo(() => {
    const value = calculateChannelMTTFd(['input', 'logic', 'output']);
    const cappedValue = value > 100 ? 100 : value;
    const mttfCategory = getMTTFCategory(cappedValue);
    return { value: cappedValue, mttfCategory };
  }, [calculateChannelMTTFd]);

  // Half Channel MTTF calculation (TE, OTE combined)
  const mttfdHalfChannel = useMemo(() => {
    if (!shouldShowTeOte) {
      return { value: 0, mttfCategory: null };
    }
    const value = calculateChannelMTTFd(['te', 'ote']);
    const cappedValue = value > 100 ? 100 : value;
    const mttfCategory = getMTTFCategory(cappedValue);
    return { value: cappedValue, mttfCategory };
  }, [calculateChannelMTTFd, shouldShowTeOte]);

  // Channel 2 MTTF calculation (Input2, Logic2, Output2 combined)
  const mttfdChannel2 = useMemo(() => {
    if (!shouldShowChannel2) {
      return { value: 0, mttfCategory: null };
    }
    const value = calculateChannelMTTFd(['input2', 'logic2', 'output2']);
    const cappedValue = value > 100 ? 100 : value;
    const mttfCategory = getMTTFCategory(cappedValue);
    return { value: cappedValue, mttfCategory };
  }, [calculateChannelMTTFd, shouldShowChannel2]);

  // Final MTTFd calculation when there are multiple channels
  // Formula: Final MTTFd = 2/3 * (mttfdc1 + mttfdc2 - 1/((1/mttfdc1) + (1/mttfdc2)))
  const finalMTTFd = useMemo(() => {
    const channel1Value = mttfdChannel1.value;
    const channel2Value = shouldShowChannel2 ? mttfdChannel2.value : 0;
    const halfChannelValue = shouldShowTeOte ? mttfdHalfChannel.value : 0;

    // If only Channel 1 exists, return Channel 1 value
    if (channel1Value > 0 && channel2Value === 0 && halfChannelValue === 0) {
      return { value: channel1Value, mttfCategory: mttfdChannel1.mttfCategory };
    }

    // If Channel 1 and Channel 2 exist, use multi-channel formula
    if (channel1Value > 0 && channel2Value > 0) {
      const finalValue = calculateMultiChannelMTTFd(channel1Value, channel2Value);
      const mttfCategory = getMTTFCategory(finalValue);
      return { value: finalValue, mttfCategory };
    }

    // If Channel 1 and Half Channel exist, use multi-channel formula
    if (channel1Value > 0 && halfChannelValue > 0) {
      const finalValue = calculateMultiChannelMTTFd(channel1Value, halfChannelValue);
      const mttfCategory = getMTTFCategory(finalValue);
      return { value: finalValue, mttfCategory };
    }

    // Default: return Channel 1 or 0
    return channel1Value > 0 
      ? { value: channel1Value, mttfCategory: mttfdChannel1.mttfCategory }
      : { value: 0, mttfCategory: null };
  }, [mttfdChannel1, mttfdChannel2, mttfdHalfChannel, shouldShowChannel2, shouldShowTeOte]);

  // Add new component row
  const addComponent = useCallback((type) => {
    const newRow = {
      tempId: `temp_${Date.now()}`,
      part_number: '',
      part_description: '',
      mttfi: '',
      dangerous_failure: '50',
      mttfdij: '',
      quantity: '',
      component_type: type,
    };

    setNewRows(prev => ({
      ...prev,
      [type]: [...prev[type], newRow],
    }));
  }, []);

  // Handle new row field changes
  const handleNewRowChange = useCallback((type, tempId, key, value) => {
    setNewRows(prev => ({
      ...prev,
      [type]: prev[type].map(row => {
        if (row.tempId === tempId) {
          const updatedRow = {...row, [key]: value};
          
          // Auto-calculate mttfdij when mttfi or dangerous_failure_pct changes
          // Formula: mttfdij = mttfi / (dfpctVal / 100)
          // Default dangerous_failure to 50% if empty
          const mttfiVal = key === 'mttfi' ? parseFloat(value) : parseFloat(updatedRow.mttfi);
          const dfpctValRaw = key === 'dangerous_failure' ? value : updatedRow.dangerous_failure;
          const dfpctVal = dfpctValRaw === '' || dfpctValRaw === null || dfpctValRaw === undefined 
            ? 50 
            : parseFloat(dfpctValRaw);
          const hasMttfi = !isNaN(mttfiVal) && mttfiVal >= 0;
          const hasDfPct = !isNaN(dfpctVal) && dfpctVal >= 1 && dfpctVal <= 100;
          if (hasMttfi && hasDfPct) {
            const calculatedMttfdij = mttfiVal / (dfpctVal / 100);
            updatedRow.mttfdij = calculatedMttfdij.toFixed(2);
          }
          
          return updatedRow;
        }
        return row;
      }),
    }));
  }, []);

  // Remove new row
  const handleRemoveNewRow = useCallback((type, tempId) => {
    setNewRows(prev => ({
      ...prev,
      [type]: prev[type].filter(r => r.tempId !== tempId),
    }));
  }, []);

  // Save all new rows
  const handleSaveAllNewRows = useCallback(async () => {
    const allNewRows = [
      ...newRows.input,
      ...newRows.logic,
      ...newRows.output,
      ...newRows.te,
      ...newRows.ote,
      ...newRows.input2,
      ...newRows.logic2,
      ...newRows.output2,
    ];

    if (allNewRows.length === 0) {
      return;
    }

    if (!assessment?.category) {
      toast.error('Please fill in Category before saving.', {
        autoClose: 2000,
        className: 'toast-error',
      });
      return;
    }

    const hasEmptyFields = allNewRows.some(row =>
      !row.part_number ||
      !row.part_description ||
      !row.mttfi ||
      !row.dangerous_failure ||
      !row.mttfdij ||
      !row.quantity
    );

    if (hasEmptyFields) {
      toast.error('Please fill in all fields before saving.', {
        autoClose: 2000,
        className: 'toast-error',
      });
      return;
    }

    const payloads = allNewRows.map(row => ({
      part_number: row.part_number,
      part_description: row.part_description,
      mttfi: parseFloat(row.mttfi),
      dangerous_failure: parseFloat(row.dangerous_failure),
      mttfdij: parseFloat(row.mttfdij),
      quantity: parseInt(row.quantity),
      component_type: row.component_type,
    }));

    await createMTTF(payloads);

    setNewRows({
      input: [],
      logic: [],
      output: [],
      te: [],
      ote: [],
      input2: [],
      logic2: [],
      output2: [],
    });
  }, [newRows, assessment?.category, createMTTF]);

  // Check if there are unsaved rows
  const hasUnsavedRows = useMemo(() => {
    return (
      newRows.input.length > 0 ||
      newRows.logic.length > 0 ||
      newRows.output.length > 0 ||
      newRows.te.length > 0 ||
      newRows.ote.length > 0 ||
      newRows.input2.length > 0 ||
      newRows.logic2.length > 0 ||
      newRows.output2.length > 0
    );
  }, [newRows]);

  // Start editing a row
  const handleStartEdit = useCallback((type, row) => {
    setEditingRow({ type, id: row.id });
    setEditedData({...row});
  }, []);

  // Handle edit field changes
  const handleEditChange = useCallback((key, value) => {
    setEditedData(prev => {
      const updatedData = {...prev, [key]: value};
      
      // Auto-calculate mttfdij when mttfi or dangerous_failure_pct changes
      // Formula: mttfdij = mttfi / (dfpctVal / 100)
      // Default dangerous_failure to 50% if empty
      const mttfiVal = key === 'mttfi' ? parseFloat(value) : parseFloat(updatedData.mttfi);
      const dfpctValRaw = key === 'dangerous_failure' ? value : updatedData.dangerous_failure;
      const dfpctVal = dfpctValRaw === '' || dfpctValRaw === null || dfpctValRaw === undefined 
        ? 50 
        : parseFloat(dfpctValRaw);
      const hasMttfi = !isNaN(mttfiVal) && mttfiVal >= 0;
      const hasDfPct = !isNaN(dfpctVal) && dfpctVal >= 1 && dfpctVal <= 100;
      if (hasMttfi && hasDfPct) {
        const calculatedMttfdij = mttfiVal / (dfpctVal / 100);
        updatedData.mttfdij = calculatedMttfdij.toFixed(2);
      }
      
      return updatedData;
    });
  }, []);

  // Save edited row
  const handleSaveEdit = useCallback(async () => {
    if (!editingRow) return;

    const payload = {
      part_number: editedData.part_number,
      part_description: editedData.part_description,
      mttfi: editedData.mttfi,
      dangerous_failure: editedData.dangerous_failure,
      mttfdij: editedData.mttfdij,
      quantity: editedData.quantity,
      component_type: editedData.component_type,
    };

    await updateMTTF(editingRow.id, payload);
    setEditingRow(null);
    setEditedData({});
  }, [editingRow, editedData, updateMTTF]);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingRow(null);
    setEditedData({});
  }, []);

  // Delete a row
  const handleDelete = useCallback(async (id) => {
    await deleteMTTF(id);
  }, [deleteMTTF]);

  return {
    // State
    newRows,
    editingRow,
    editedData,
    
    // Flags
    shouldShowTeOte,
    shouldShowChannel2,
    hasUnsavedRows,
    
    // Calculations
    mttfdChannel1,
    mttfdHalfChannel,
    mttfdChannel2,
    finalMTTFd,
    
    // Actions
    addComponent,
    handleNewRowChange,
    handleRemoveNewRow,
    handleSaveAllNewRows,
    handleStartEdit,
    handleEditChange,
    handleSaveEdit,
    handleCancelEdit,
    handleDelete,
    
    // Loading states
    isCreating,
    isUpdating,
    isDeleting,
  };
};

export default useMTTF;

